// Page handling class allows us to organize page data and routine operations. Note that there can be two kinds of pages loaded,
// the stripped down menu pages (without layout/components/settings) and full pages objects (with all data). Only the former is cached.
// Context (mote object returned from the server) can be optionally included in the reply - this is stored in our full page objects (as it affects the page render - but cannot be cached).

import GroupCache from '@/groups';
import { SchemaCache } from '@/schemas';
import { Mote } from '@/mote';

class Page {
    id = 0;
    title = '';
    slug = '';
    sequence = 0;
    layout = null;
    components = null;
    settings = null;
    user_id = 0;
    group_id = 0;
    created_at = null;
    updated_at = null;
    context = null;
    
    // Constructor for loading page object from server data. You can't create a new empty page this way; see page-create.vue for that.
    constructor(record) {
        for (let key in record) {
            if (key in this) this[key] = record[key];
        }
        this.preprocess();
    }

    // Preprocess page data before making object available.
    preprocess() {
        // Deserialize JSON data. We accept 'stringified' JSON data as well, even though our API endpoint should always return the expanded JSON.
        // i.e. the difference between { title: 'abc', layout: { 123:4, 567:8 }} and { title: 'abc', layout: '{123:4,567:8}' }.
        for (let key of [ 'layout', 'components', 'settings' ]) {
            if (!this[key]) {
                this[key] = {};
            } else if (typeof this[key] == 'string') {
                console.warn("Forcibly converting stringified JSON to object for page", this.id, key);
                this[key] = JSON.parse(this[key]);
            } else if (typeof this[key] == 'object' && !Array.isArray(this[key])) { 
                // No need to do anything, already in the correct format.
            } else if (typeof this[key] == 'object' && this[key].length <= 0) {
                // We allow only empty 'arrays' as objects. This happens with empty data from the API (e.g. {}).
                this[key] = {};
            } else {
                console.error("Invalid type detected in page", this.id, key, typeof this[key], this[key]);
                throw new Error(`Invalid type detected in page ${this.id} for ${key}: ${typeof this[key]}`);
            }
        }
        if (this.context && this.context.id) {
            this.context = new Mote(this.context);
        }
    }

    // Get group object for this page easily, returning null if not found.
    getGroup() {
        return GroupCache.getById(this.group_id);
    }

    // Get slug path for this page, including the group slug. Note that this does NOT include a leading slash, which you normally want.
    getSlugPath() {
        const group = this.getGroup();
        return group ? `${group.slug}/${this.slug}` : null;
    }

    // Get the highest layoutUid in our layout tree. Add 1 to this value to get the next valid layoutUid.
    // If overrideLayout is provided, use that instead of the page's layout. This is useful if we're dealing with default or supplied layout trees
    findHighestLayoutUid(overrideLayout = null) {
        let highestUid = 0;
        function _traverse(node) {
            if (node.id > highestUid)
                highestUid = node.id;
            if (node.items && Array.isArray(node.items)) {
                for (const item of node.items) {
                    _traverse(item);
                }
            }
        }
        _traverse(overrideLayout || this.layout);
        return highestUid;
    }

    // Return if we should be using a context object for this page.
    shouldUseContext() {
        return this.settings?.useContext ?? false;
    }
    // Get all possible context schemas that can be used for this page.
    getContextSchemas() {
        if (!this.settings?.useContext || !this.settings.contextSchemas || !Array.isArray(this.settings.contextSchemas))
            return [];
        return this.settings.contextSchemas.map(schema => SchemaCache.getSchemaByType(schema));
    }
    // Return if we have an actual valid context object for this page (as loaded mote). This also tests that the context is valid for the page settings.
    hasValidContext() {
        if (this.shouldUseContext() && this.context && this.context.id) {
            let moteSchema = this.context.getMoteSchema();
            if (moteSchema && this.getContextSchemas().map(s => s.id).includes(moteSchema.id)) {
                return true;
            }
        }
        return false;
    }

}

export default Page;
